import styles from "./investment.module.css"
import React, {useEffect} from "react"
import {Wizard} from "activities/components/wizard"
import * as finance from "shared/finance"
import * as util from "activities/util"
import * as shared from "activities/components/shared"
import * as inputs from "activities/components/inputs"
import * as callouts from "activities/components/callouts"
import {DoubleBar} from "story/components/graphs/bar"
import * as graphUtil from "shared/graphs/util"
import AccessibilityTable from "activities/components/shared/accessibility-table"
import cx from "classnames"

const getTotal = (investmentAmount, investmentFrequency, years, rate, compoundingFrequency) => {
  const _rate = rate / 100
  const frequencyIndex = {
    daily: 365,
    monthly: 12,
    annually: 1
  }
  const contributionFrequencyIndex = {
    "once": 0,
    "monthly": 12,
    "twice-a-year": 2,
    "annually": 1
  }

  const compoundingPeriodsPerYear = frequencyIndex[compoundingFrequency]
  const contributionPeriodsPerYear = contributionFrequencyIndex[investmentFrequency]
  const _rateDividedByCompoundingFrequency = _rate / compoundingPeriodsPerYear
  const totalCompoundingPeriods = years * compoundingPeriodsPerYear

  const compoundInterestFormula = investmentAmount * Math.pow(1 + _rateDividedByCompoundingFrequency, totalCompoundingPeriods)

  // If no regular contributions, return just the compound interest
  if (contributionPeriodsPerYear === 0) {
    return compoundInterestFormula
  }

  const contributionAmount = investmentAmount
  const totalContributionPeriods = years * contributionPeriodsPerYear

  const periodsPerContribution = compoundingPeriodsPerYear / contributionPeriodsPerYear
  const contributionsFutureValue =
    contributionAmount *
    ((Math.pow(1 + _rateDividedByCompoundingFrequency, totalCompoundingPeriods) - 1) / (Math.pow(1 + _rateDividedByCompoundingFrequency, periodsPerContribution) - 1))

  return compoundInterestFormula + contributionsFutureValue
}

const genData = (investmentAmount, investmentFrequency, years, rateType, rateMap, compoundingFrequency) => {
  const {fixedRate, lowRate, avgRate, highRate} = rateMap
  const _getTotal = (y, r) => getTotal(investmentAmount, investmentFrequency, y, r, compoundingFrequency)
  return util.xAxisArray(years).reduce((memo, y) => {
    const bars = rateType === "range" ? [_getTotal(y, lowRate), _getTotal(y, avgRate), _getTotal(y, highRate)] : [_getTotal(y, fixedRate)]
    memo.push([y, ...bars])
    return memo
  }, [])
}

const calculate = (investmentAmount, investmentFrequency, years, rateType, rateMap, compoundingFrequency) => {
  const {fixedRate, lowRate, avgRate, highRate} = rateMap
  const totalReturn = getTotal(investmentAmount, investmentFrequency, years, rateType === "range" ? avgRate : fixedRate, compoundingFrequency)

  const contributionFrequencyIndex = {
    "once": 0,
    "monthly": 12,
    "twice-a-year": 2,
    "annually": 1
  }
  const contributionPeriodsPerYear = contributionFrequencyIndex[investmentFrequency]
  const totalContributions =
    contributionPeriodsPerYear === 0
      ? investmentAmount // one-time investment
      : investmentAmount * (1 + contributionPeriodsPerYear * years) // initial + regular contributions

  return {
    data: genData(investmentAmount, investmentFrequency, years, rateType, rateMap, compoundingFrequency),
    totalReturn,
    interestEarned: totalReturn - totalContributions,
    totalContributions
  }
}

const BasicInvestmentDetails = ({updateState, state}) => {
  const {investmentAmount, investmentFrequency, years} = state
  return (
    <div className={styles["first-page"]}>
      <inputs.CalcInput id="investment-amount" title="Investment Amount" onChange={updateState("investmentAmount", util.fromString)} initialValue={investmentAmount} />
      <div>
        <inputs.Label inputId="investment-frequency">Investment Frequency</inputs.Label>
        <inputs.Dropdown
          id="investment-frequency"
          options={[
            ["once", "Once"],
            ["monthly", "Monthly"],
            ["twice-a-year", "Twice a Year"],
            ["annually", "Annually"]
          ]}
          initialValue={investmentFrequency}
          onChange={updateState("investmentFrequency")}
          className={styles["dropdown"]}
        />
      </div>
      <inputs.RangeInput
        id="years"
        title="Number of Years"
        onChange={updateState("years", util.fromString)}
        initialValue={years}
        min={5}
        max={50}
        step={5}
        units="years"
        valueType="number"
      />
    </div>
  )
}

const AdvancedInvestmentDetails = ({updateState, state, props}) => {
  const {investmentAmount, investmentFrequency, rateType, fixedRate, highRate, lowRate, compoundingFrequency, years} = state
  const {theme} = props
  const isRangeRate = rateType === "range"
  const isFixedRate = rateType === "fixed"

  const {data, totalReturn, interestEarned, totalContributions} = calculate(
    investmentAmount,
    investmentFrequency,
    years,
    rateType,
    {fixedRate, lowRate, avgRate: (lowRate + highRate) / 2, highRate},
    compoundingFrequency
  )

  const tooltipFields = isRangeRate
    ? [
        {name: "Low Rate Total", type: "currency", color: graphUtil.getColor(theme, 1)},
        {name: "Average Rate Total", type: "currency", color: graphUtil.getColor(theme, 2)},
        {name: "High Rate Total", type: "currency", color: graphUtil.getColor(theme, 3)}
      ]
    : [{name: "Total with Interest", type: "currency", color: graphUtil.getColor(theme, 1)}]

  const tooltipStyles = {width: 200, rows: isRangeRate ? 4 : 2}

  const accessibilityTableHeadings = isRangeRate
    ? [
        {name: "Year", key: "year"},
        {name: "Low Rate Balance", key: "lowRate"},
        {name: "Average Rate Balance", key: "avgRate"},
        {name: "High Rate Balance", key: "highRate"}
      ]
    : [
        {name: "Year", key: "year"},
        {name: "Balance", key: "balance"}
      ]

  const accessibilityTableData = data.map((row) => {
    const [year, ...values] = row
    return isRangeRate
      ? {
          year,
          lowRate: util.formatDollarsNoCents(values[0]),
          avgRate: util.formatDollarsNoCents(values[1]),
          highRate: util.formatDollarsNoCents(values[2])
        }
      : {
          year,
          balance: util.formatDollarsNoCents(values[0])
        }
  })

  const accessibilityTableLabel = "Investment Growth Over Time"

  const handleRateChange = (rateName, v) => {
    if (v && v > 0) {
      return updateState(rateName, parseFloat)(v)
    }
  }

  return (
    <div className={styles["second-page"]}>
      <shared.SummaryCalcContainer className={styles["summary-calc-container"]}>
        <shared.ChartContainer className={styles["chart"]}>
          <DoubleBar
            isStacked={true}
            values={data}
            yAxis="Balance"
            formatter="currency"
            tooltip={["Year", ...tooltipFields]}
            customTooltipStyles={tooltipStyles}
            type="calculator"
            colors={[graphUtil.getColor(theme, 1), graphUtil.getColor(theme, 2), graphUtil.getColor(theme, 3)]}
            ariaLabel="This bar graph visually represents how much you can save in a money market account. Read below to find the total balance, interest earned over time, and the total contributions made in the account."
          />
        </shared.ChartContainer>
        <shared.SummaryContainer className={styles["summary"]}>
          <callouts.Callout calloutStyles={styles["callout"]}>
            <callouts.Detail
              statStyles={styles["stat"]}
              labelStyles={styles["label"]}
              label={isRangeRate ? "Average Interest Earned" : "Interest Earned"}
              result={util.formatDollarsNoCents(interestEarned)}
              dataTestId="interest-earned"
            />
            <callouts.Detail
              statStyles={styles["stat"]}
              labelStyles={styles["label"]}
              label="Total Amount Invested"
              result={util.formatDollarsNoCents(totalContributions)}
              dataTestId="total-contributions"
            />
            <callouts.Detail
              statStyles={styles["stat"]}
              labelStyles={styles["label"]}
              label={isRangeRate ? "Average Total (Investment + Interest)" : "Total (Investment + Interest)"}
              result={util.formatDollarsNoCents(totalReturn)}
              dataTestId="total-investment"
            />
          </callouts.Callout>
        </shared.SummaryContainer>
        <shared.InputsContainer className={styles["inputs-container"]}>
          <div>
            <inputs.Label inputId="compounding-frequency">Interest Compound Frequency</inputs.Label>
            <inputs.Dropdown
              id="compounding-frequency"
              options={[
                ["daily", "Daily"],
                ["monthly", "Monthly"],
                ["annually", "Annually"]
              ]}
              initialValue={compoundingFrequency}
              onChange={updateState("compoundingFrequency")}
              className={styles["dropdown"]}
            />
          </div>
          <div>
            <inputs.Label inputId="rate-type">Interest Rate</inputs.Label>
            <inputs.Tabs inputId="rate-type">
              <inputs.Tab selected={isFixedRate} handleClick={() => updateState("rateType")("fixed")}>
                Fixed
              </inputs.Tab>
              <inputs.Tab selected={isRangeRate} handleClick={() => updateState("rateType")("range")}>
                Range
              </inputs.Tab>
            </inputs.Tabs>
          </div>
          {isFixedRate && (
            <inputs.RangeInput
              id="fixed-rate"
              title="Fixed Interest Rate"
              onChange={(v) => handleRateChange("fixedRate", v)}
              valueType="percent"
              initialValue={fixedRate || 0}
              inputCls={styles["direct-text-input"]}
              min={0}
              max={20}
              step={0.5}
            />
          )}
          {isRangeRate && (
            <div className={styles["input-grid"]}>
              <inputs.RangeInput
                id="high-rate"
                title="High Interest Rate"
                onChange={(v) => handleRateChange("highRate", v)}
                initialValue={highRate || 0}
                inputCls={styles["direct-text-input"]}
                valueType="percent"
                min={0}
                max={20}
                step={0.5}
              />
              <inputs.RangeInput
                id="low-rate"
                title="Low Interest Rate"
                onChange={(v) => handleRateChange("lowRate", v)}
                initialValue={lowRate || 0}
                inputCls={styles["direct-text-input"]}
                valueType="percent"
                min={0}
                max={20}
                step={0.5}
              />
            </div>
          )}
        </shared.InputsContainer>
      </shared.SummaryCalcContainer>
      <AccessibilityTable headings={accessibilityTableHeadings} data={accessibilityTableData} label={accessibilityTableLabel} />
    </div>
  )
}

const InvestmentCalculator_ = shared.ActivityFactory(
  ({getProps, getState, updateState, setState, onReset}) => {
    const {resetKey} = getState()
    const {steps, theme} = getProps()

    return (
      <div className={styles["wizard"]}>
        <Wizard
          resetKey={resetKey}
          onReset={onReset}
          setup={{
            steps,
            theme,
            props: {
              className: styles["investment-calculator"],
              setState,
              updateState,
              state: getState(),
              props: getProps()
            }
          }}
        />
      </div>
    )
  },
  {
    defaultProps: {
      className: styles["investment-calculator"],
      isWizardCalculator: true,
      steps: [
        {
          title: "Investment Calculator",
          subtitle: `Estimate how much you could earn from a one time\nor regular series of investments.`,
          content: BasicInvestmentDetails,
          isStepComplete: ({state}) => {
            const {investmentAmount, investmentFrequency} = state
            return investmentAmount > 0 && investmentFrequency
          },
          stepStyles: cx(styles["step"], styles["one"]),
          buttonText: "Next"
        },
        {
          title: "Investment Calculator",
          subtitle: `Estimate how much you could earn from a one time\nor regular series of investments.`,
          content: AdvancedInvestmentDetails,
          isStepComplete: () => true,
          stepStyles: cx(styles["step"], styles["two"]),
          buttonText: "Start Over"
        }
      ]
    },
    defaultState: {
      investmentAmount: 100,
      investmentFrequency: "once",
      compoundingFrequency: "monthly",
      years: 30,
      rateType: "fixed",
      fixedRate: 5.5,
      highRate: 8,
      lowRate: 2
    }
  }
)

export default (props) => <InvestmentCalculator_ {...props} />
